import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../adminApi";
import Dashboard from "../components/Dashboard";
import Transactions from "../components/Transactions";
import Servers from "../components/Servers";
import PromoCodes from "../components/PromoCodes";
import CreatePromoForm from "../components/CreatePromoForm";
import Domain from "../components/Domain";

/*
  The pre-existing admin views, each wrapped with its own data loading so
  they slot into the new panel unchanged. Styling comes from .ap-legacy in
  AdminConsole.css.
*/

const formatCurrency = (amount) => `₹${(Number(amount || 0) / 100).toFixed(2)}`;
const formatDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const Page = ({ title, subtitle, action, children }) => (
  <div className="cx-page">
    <div className="cx-page-head cx-page-head-row">
      <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
      {action}
    </div>
    <div className="ap-legacy">{children}</div>
  </div>
);

const useLoad = (fn) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    fn().then((d) => { setData(d); setError(""); }).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);
  return { data, error, load };
};

const ErrorNote = ({ error }) => (error ? <div className="cx-alert is-error">{error}</div> : null);

export const DashboardSection = () => {
  const stats = useLoad(() => adminApi("/dashboardStats").then((d) => (Array.isArray(d) ? d[0] : d)));
  const activity = useLoad(() => adminApi("/getRecentActivity"));
  const support = useLoad(() => adminApi("/support/summary"));
  const s = support.data;
  return (
    <Page title="Dashboard" subtitle="Platform at a glance.">
      <ErrorNote error={stats.error} />
      {s && (
        <div className="cx-alert is-info">
          <span>
            {!s.configured
              ? "Support tickets: Zendesk isn't connected yet (set the ZENDESK_* environment variables)."
              : s.openTickets === null
                ? "Support tickets: couldn't reach Zendesk just now."
                : `${s.openTickets} unsolved support ticket${s.openTickets === 1 ? "" : "s"} in Zendesk.`}
          </span>
          {s.agentUrl && <a href={s.agentUrl} target="_blank" rel="noreferrer" className="cx-link">Open Zendesk</a>}
        </div>
      )}
      <Dashboard dashboardStats={stats.data || {}} recentActivity={activity.data || []} formatCurrency={formatCurrency} formatDate={formatDate} />
    </Page>
  );
};

// Older abandoned checkouts were saved as cancelled; they count as failed.
const TX_FILTERS = [["all", "All"], ["completed", "Completed"], ["pending", "Pending"], ["failed", "Failed"]];
const txGroup = (s) => (s === "cancelled" ? "failed" : s);

export const TransactionsSection = () => {
  const { data, error } = useLoad(() => adminApi("/getAllTransactions"));
  const [filter, setFilter] = useState("all");
  const all = Array.isArray(data) ? data : [];
  const count = (id) => all.filter((t) => id === "all" || txGroup(t.status) === id).length;
  const shown = all.filter((t) => filter === "all" || txGroup(t.status) === filter);
  return (
    <Page title="Transactions" subtitle="Every account balance credit and debit. A payment not completed within 3 hours is marked failed.">
      <ErrorNote error={error} />
      <div className="ap-subtabs" role="tablist" aria-label="Filter by status">
        {TX_FILTERS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={filter === id} className={`ap-subtab${filter === id ? " is-active" : ""}`}
            onClick={() => setFilter(id)}>{label} ({count(id)})</button>
        ))}
      </div>
      <Transactions transactions={shown} formatCurrency={formatCurrency} formatDate={formatDate} />
    </Page>
  );
};

export const PromoCodesSection = ({ notify }) => {
  const { data, error, load } = useLoad(() => adminApi(`/promo-codes?t=${Date.now()}`).then((d) => d.promoCodes || d));
  const [creating, setCreating] = useState(false);
  const [codes, setCodes] = useState(null);
  const list = codes || data || [];

  const remove = async (id) => {
    if (!window.confirm("Delete this promo code?")) return;
    try {
      await adminApi(`/promo-codes/${id}`, { method: "DELETE" });
      notify("Promo code deleted", "success");
      setCodes(null);
      load();
    } catch (e) {
      notify(e.message, "error");
    }
  };

  return (
    <Page
      title="Promo codes"
      subtitle="Account balance credit codes for customers."
    >
      <ErrorNote error={error} />
      <PromoCodes
        promoCodes={list}
        setShowCreatePromo={setCreating}
        deletePromoCode={remove}
        formatDate={formatDate}
        onPromoUpdate={(updated) => setCodes(list.map((p) => (p.id === updated.id ? updated : p)))}
      />
      {creating && (
        <CreatePromoForm
          onSuccess={() => { setCreating(false); setCodes(null); load(); notify("Promo code created", "success"); }}
          onCancel={() => setCreating(false)}
        />
      )}
    </Page>
  );
};

export const ServersSection = () => {
  const { data, error } = useLoad(() => adminApi("/getAllServers"));
  return (
    <Page title="Virtual Machines" subtitle="Every customer virtual machine.">
      <ErrorNote error={error} />
      <Servers servers={data || []} formatCurrency={formatCurrency} formatDate={formatDate} />
    </Page>
  );
};

export const DomainsSection = () => (
  <Page title="Domains" subtitle="Domains registered through Walras Cloud.">
    <Domain />
  </Page>
);
